import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { renameContent } from "../../../Redux/Actions/contentAction";
import { tr } from "../../../i18n";

/**
 * Content card title. Click to rename (Enter saves, Esc cancels); read-only users just see the name.
 */
export default function ContentName({ data, readOnly = false, maxLength = 20, className }) {
  const dispatch = useDispatch();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(data?.name || "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const name = data?.name || "";

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      // Select the name without the extension, like a file manager
      const dot = name.lastIndexOf(".");
      inputRef.current.setSelectionRange(0, dot > 0 ? dot : name.length);
    }
  }, [editing, name]);

  const start = (e) => {
    if (readOnly) return;
    e.stopPropagation();
    setValue(name);
    setEditing(true);
  };

  const save = async () => {
    const next = value.trim();
    if (!next || next === name) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await dispatch(renameContent(data.id, next));
      setEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not rename");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <p className={className} onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          value={value}
          maxLength={200}
          disabled={saving}
          onChange={(e) => setValue(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") setEditing(false);
          }}
          aria-label={tr("Name")}
          style={{
            width: "100%",
            minWidth: 0,
            font: "inherit",
            padding: "2px 6px",
            border: "1px solid var(--primary-color)",
            borderRadius: 4,
            outline: "none",
          }}
        />
      </p>
    );
  }

  return (
    <p
      className={className}
      title={readOnly ? name : `${name}\nClick to rename`}
      onClick={start}
      style={readOnly ? undefined : { cursor: "text" }}
    >
      {name.length > maxLength ? `${name.substring(0, maxLength)}…` : name}
    </p>
  );
}
